
import React, { useState } from "react";
import { CampaignData } from "./CampaignBuilder";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SendIcon, Play, Calendar, UserRound, Mail, ChevronDown, AlertTriangle } from "lucide-react";
import { useEmailSender } from "@/hooks/useEmailSender";
import { format } from "date-fns";
import { useCampaigns } from "@/hooks/useCampaigns";
import { useNavigate } from "react-router-dom";
import { MockBadge } from "@/components/ui/mock-badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface CampaignPreviewProps {
  campaignData: CampaignData;
}

export const CampaignPreview: React.FC<CampaignPreviewProps> = ({ campaignData }) => {
  const [testEmail, setTestEmail] = useState("");
  const [isScheduling, setIsScheduling] = useState(false);
  const { sendTestEmail, isSendingTest: isSendingTestEmail } = useEmailSender();
  const { saveCampaign } = useCampaigns();
  const navigate = useNavigate();

  const handleSendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      await sendTestEmail({
        to: testEmail,
        subject: campaignData.subject,
        html: campaignData.content || "<p>No content available</p>",
        previewText: campaignData.previewText,
      });
    } catch (error) {
      console.error("Error sending test email:", error);
      // Error handling is done in the mutation
    }
  };

  const handleScheduleCampaign = async () => {
    if (!campaignData.name) {
      toast.error("Please provide a campaign name");
      return;
    }

    if (!campaignData.subject) {
      toast.error("Please provide a subject line");
      return;
    }

    if (!campaignData.content) {
      toast.error("Please add content to your campaign");
      return;
    }

    if (campaignData.audienceSegments.length === 0 && !campaignData.selectedSubscriberIds?.length) {
      toast.error("Please select at least one segment or subscriber");
      return;
    }

    setIsScheduling(true);
    try {
      // Determine status based on send date
      const status = campaignData.sendDate ? "scheduled" : "sending";
      
      await saveCampaign({
        ...campaignData,
        status: status as "draft" | "scheduled" | "sending" | "sent" | "failed"
      });
      
      toast.success(
        campaignData.sendDate 
          ? "Campaign scheduled successfully" 
          : "Campaign queued for sending"
      );
      
      navigate("/dashboard/newsletter?tab=campaigns");
    } catch (error) {
      console.error("Error scheduling campaign:", error);
      toast.error("Failed to schedule campaign");
    } finally {
      setIsScheduling(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Development Mode Banner */}
      <Card className="border-amber-300 bg-amber-50">
        <CardContent className="p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <div>
            <h4 className="text-sm font-medium text-amber-800">Development/Demo Mode</h4>
            <p className="text-xs text-amber-700 mt-1">
              This campaign preview is using simulated data and mock connections. 
              No real emails will be sent, and analytics will use placeholder data.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Campaign Preview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 border rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Campaign Details</h3>
              
              <div className="space-y-1">
                <p className="text-xs text-slate-500">Name</p>
                <p className="text-sm font-medium">{campaignData.name || "Untitled Campaign"}</p>
              </div>
              
              <div className="space-y-1">
                <p className="text-xs text-slate-500">Subject</p>
                <p className="text-sm font-medium">{campaignData.subject || "No subject"}</p>
              </div>
              
              <div className="space-y-1">
                <p className="text-xs text-slate-500">Preview Text</p>
                <p className="text-sm font-medium">{campaignData.previewText || "No preview text"}</p>
              </div>
            </div>
            
            <div className="flex-1 border rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Delivery Settings</h3>
              
              <div className="space-y-1">
                <p className="text-xs text-slate-500">Schedule</p>
                <div className="flex items-center">
                  {campaignData.sendDate ? (
                    <p className="text-sm font-medium flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-slate-500" />
                      {format(campaignData.sendDate, "PPP")} at {format(campaignData.sendDate, "p")}
                    </p>
                  ) : (
                    <p className="text-sm font-medium flex items-center">
                      <Play className="h-4 w-4 mr-1 text-slate-500" />
                      Send immediately
                    </p>
                  )}
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-xs text-slate-500">Recipients</p>
                <div className="flex items-center gap-2">
                  <UserRound className="h-4 w-4 mr-1 text-slate-500" />
                  <p className="text-sm font-medium">
                    {campaignData.selectedSubscriberIds?.length || "All"} subscribers in {campaignData.audienceSegments.length || 0} segments
                  </p>
                  <MockBadge tooltip="Audience segments contain mock data. No real subscribers will be affected." />
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-xs text-slate-500">Tracking</p>
                <div className="flex flex-wrap gap-2">
                  {campaignData.trackOpens && (
                    <Badge variant="outline" className="text-xs bg-slate-50">Opens tracking enabled</Badge>
                  )}
                  {campaignData.trackClicks && (
                    <Badge variant="outline" className="text-xs bg-slate-50">Clicks tracking enabled</Badge>
                  )}
                  {!campaignData.trackOpens && !campaignData.trackClicks && (
                    <Badge variant="outline" className="text-xs bg-slate-50">No tracking enabled</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Accordion type="single" collapsible className="border rounded-lg overflow-hidden">
            <AccordionItem value="content" className="border-0">
              <AccordionTrigger className="px-4 py-3 hover:bg-slate-50 text-sm font-medium">
                Email Content Preview
              </AccordionTrigger>
              <AccordionContent className="border-t">
                <div className="bg-white p-4 rounded">
                  <div className="prose prose-slate max-w-none overflow-auto max-h-[400px] p-4 border rounded">
                    {campaignData.content ? (
                      <div dangerouslySetInnerHTML={{ __html: campaignData.content }} />
                    ) : (
                      <p className="text-slate-500 italic">No content available</p>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader className="pb-3 flex flex-row justify-between items-center">
          <CardTitle className="text-base">Send Test Email</CardTitle>
          <MockBadge tooltip="Test emails in development mode won't actually be sent to any recipients. This is a simulated feature." />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Label htmlFor="test-email" className="sr-only">
                Test Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="test-email"
                  placeholder="Enter email address"
                  type="email"
                  className="pl-9 border-slate-200"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>
            </div>
            <Button
              variant="outline"
              className="border-slate-200"
              onClick={handleSendTest}
              disabled={isSendingTestEmail || !testEmail}
            >
              {isSendingTestEmail ? "Sending..." : "Send Test"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-3">
        <Button
          variant="outline"
          className="border-slate-200"
          onClick={() => navigate("/dashboard/newsletter?tab=campaigns")}
        >
          Cancel
        </Button>
        <Button
          onClick={handleScheduleCampaign}
          disabled={isScheduling}
          className="bg-slate-900 hover:bg-slate-800"
        >
          <SendIcon className="h-4 w-4 mr-2" />
          {isScheduling
            ? "Processing..."
            : campaignData.sendDate
            ? "Schedule Campaign"
            : "Send Campaign Now"}
        </Button>
      </div>
    </div>
  );
};
